import { HttpClient, HttpHeaders, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { BehaviorSubject, Subject, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import * as fromRoot from '../../state/app.state';
import * as SharedActions from '../core/shared.actions';
import { ClearState as ClearSharedState } from '../core/shared.actions';
import { ClearState as ClearProjectState } from '../../modules/projects/core/projects.actions';
import { ClearState as ClearSearchState } from '../../modules/search/core/search.actions';
import { FormGroup } from '@angular/forms';
import { MatSnackBar as MatSnackBar } from '@angular/material/snack-bar';

/** Regex for valid email: local@domain.tld (e.g. user@example.com). Use in Validators.pattern(). */
export const EMAIL_PATTERN = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

@Injectable({
  providedIn: 'root',
})
export class CommonService {
  /** @see EMAIL_PATTERN */
  readonly emailPattern = EMAIL_PATTERN;

  triggerAddDocument: Subject<any> = new Subject<any>();
  removeReportId: Subject<any> = new Subject<any>();
  closeSearchPopup: Subject<any> = new Subject<any>();
  /**
   * Full /search URL (query + tab + filters) the user left by opening a
   * result; the header shows a "Back to search results" link while set.
   * Cleared when the user lands on /search again.
   */
  searchResultsUrl: string | null = null;
  closeLoadingScreen: Subject<null> = new Subject<null>();
  uploadedFilesInfo: Subject<any[]> = new Subject<any[]>();
  uploadedFilesInfoOnPopup: Subject<any[]> = new Subject<any[]>();
  resetPaginator: Subject<any> = new Subject<any>();
  closeDialog: Subject<void> = new Subject<void>();
  openSuccessDialog: Subject<void> = new Subject<void>();
  clearRowSelection: Subject<void> = new Subject<void>();
  focusedIn: Subject<any> = new Subject<any>();
  levelThreeMarkerData: Subject<any> = new Subject<any>();
  setHeaderBgWhite: Subject<any> = new Subject<any>();
  resetSearchContext: Subject<any> = new Subject<any>();
  private levelThreeClickSubject = new Subject<boolean>();
  levelThreeClick$ = this.levelThreeClickSubject.asObservable();
  private isMarkerClickSubject = new Subject<boolean>();
  isMarkerClick$ = this.isMarkerClickSubject.asObservable();
  private currentIsMarkerClick = false; // Variable to store the current value
  private currentIslevelThreeClick = false;
  private isExpandedSubject = new BehaviorSubject<boolean>(false);
  isExpanded$ = this.isExpandedSubject.asObservable(); // Expose as observable

  private closeSideNavSubject = new BehaviorSubject<boolean>(false); // Default state is closed
  closeSideNav$ = this.closeSideNavSubject.asObservable();

  constructor(
    private store?: Store<fromRoot.State>,
    private http?: HttpClient,
    private snackBar?: MatSnackBar,
  ) {
  }

  setCloseSideNav(value: boolean) {
    this.closeSideNavSubject.next(value); // Update the state
  }

  getCloseSideNavValue(): boolean {
    return this.closeSideNavSubject.value; // Retrieve current value
  }

  setIsExpanded(value: boolean) {
    this.isExpandedSubject.next(value); // Update the state
  }

  setToSessionStorage(key: string, value: string) {
    sessionStorage.setItem(key, value);
  }

  getFromSessionStorage(key: string) {
    return sessionStorage.getItem(key);
  }

  removeFromSessionStorage(key: string) {
    sessionStorage.removeItem(key);
  }

  updateIsMarkerClick(value: boolean): void {
    this.currentIsMarkerClick = value;
    this.isMarkerClickSubject.next(value);
  }

  getCurrentIsMarkerClick(): boolean {
    return this.currentIsMarkerClick;
  }

  updatelevelThreeClick(value: boolean): void {
    this.currentIslevelThreeClick = value;
    this.levelThreeClickSubject.next(value);
  }

  getCurrentlevelThreeClick(): boolean {
    return this.currentIslevelThreeClick;
  }

  callAPI(type: string, url: string, payload?: any, options: any = {}) {
    // @ts-ignore
    let apiCall = this.http[type]<any>(url, payload, {
      observe: 'response',
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
      }),
      ...options,
    });

    if (type === 'delete') {
      apiCall = this.http[type]<any>(url, {
        observe: 'response',
        body: payload || {},
        headers: new HttpHeaders({
          'Content-Type': 'application/json',
        }),
        ...options,
      });
    }

    return apiCall.pipe(
      map((response: any) => {
        if (response instanceof HttpResponse) {
          return response.body;
        }

        return response;
      }),
      catchError(error => throwError(() => error)),
    );
  }

  startLoading() {
    this.store.dispatch(SharedActions.StartLoading());
  }

  stopLoading() {
    this.store.dispatch(SharedActions.StopLoading());
  }

  clearAppState() {
    this.store.dispatch(ClearSharedState());
    this.store.dispatch(ClearProjectState());
    this.store.dispatch(ClearSearchState());
  }

  clearStorage() {
    const appLocalStorageKeys = ['sitenotes.shared', 'sitenotes.project'];

    appLocalStorageKeys.forEach(key => {
      this.removeFromSessionStorage(key);
    });
  }

  checkFormValidation(form: FormGroup, errorMessageMap: any, currentField?: string) {
    const errorMessages = {};
    const formControls = form.controls || null;

    if (currentField) {
      const errors = form.controls[currentField]?.errors;
      const firstErrorType = errors ? Object.keys(errors)[0] : '';
      if (firstErrorType && errorMessageMap[currentField] && errorMessageMap[currentField][firstErrorType]) {
        // @ts-ignore
        errorMessages[currentField] = errorMessageMap[currentField][firstErrorType];
      }
      return errorMessages;
    }

    // tslint:disable-next-line:forin
    for (const eachControlName in formControls) {
      const isControlValid = form.controls[eachControlName].valid;
      if (!isControlValid) {
        const errors = form.controls[eachControlName].errors;
        const firstErrorType = errors ? Object.keys(errors)[0] : null;
        if (firstErrorType) {
          form.controls[eachControlName].markAsTouched();
          // @ts-ignore
          errorMessages[eachControlName] = errorMessageMap[eachControlName][firstErrorType];
        }
      }
    }
    return errorMessages;
  }

  notification(message: string, type = '', actionText?: string, duration?: number) {
    if (type === 'success') {
      message = `${message}`;
    }
    this.snackBar.open(message, actionText, {
      duration: duration ?? this.readingDuration(message),
      verticalPosition: 'top', // 'top' | 'bottom'
      horizontalPosition: 'center', // 'start' | 'center' | 'end' | 'left' | 'right'
      panelClass: [type],
    });
  }

  // 5s floor for short toasts, then ~50ms per character of reading time, capped at 15s
  private readingDuration(message: string): number {
    return Math.min(Math.max(5000, (message?.length || 0) * 50), 15000);
  }

  errorNotification(message: string, type = '', actionText?: string, duration?: number) {

    this.snackBar.open(message, actionText, {
      duration: duration ?? this.readingDuration(message),
      verticalPosition: 'bottom', // 'top' | 'bottom'
      horizontalPosition: 'center',
    });
  }
}
